"use client";
import { KeyNames } from "./filterJson";
import makeApiCall from "@packages/lib/server-actions/rest-api";
import API_URL from "@packages/lib/server-actions/api-urls";
import { setDocumentCookie } from "../cookie-utils";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { REFRESH_PARAMETERS } from "@packages/shared-components/common-utilities/popups/search-filtercomponent/filter-contant-values/filter-constants";
import {
  UNI_SORTING_MOCKDATA,
  IGNORED_PREFIXES,
} from "@packages/shared-components/common-utilities/popups/search-filtercomponent/filter-contant-values/filter-constants";
import { isPGS } from "@packages/lib/utlils/commonFunction";
import { isWhatuni } from "@packages/lib/utlils/commonFunction";
const keyName = KeyNames();
type KeyValueObject = Record<string, string>;

const parseQueryParams = (queryString: string): Record<string, string> =>
  queryString?.split("&")?.reduce((acc: Record<string, string>, param) => {
    const [k, v] = param?.split("=");
    if (k) acc[k] = decodeURIComponent(v || "");
    return acc;
  }, {});

const getSessionParams = (): KeyValueObject => {
  if (typeof window === "undefined") return {};
  return JSON.parse(sessionStorage?.getItem("filter_param") || "{}");
};

const updateMergedKeyValue = (
  merged: KeyValueObject,
  key: string,
  value: string,
) => {
  const original = merged[key];
  let valuesSet = new Set(original?.split("+"));

  if (
    valuesSet.has(value) &&
    ![keyName?.postcode, keyName?.distance].includes(key)
  ) {
    valuesSet.delete(value);
  } else if (key === keyName?.subject) {
    valuesSet.add(value);
  } else if (key === keyName?.month && value === "All month") {
    delete merged[key];
    return;
  } else {
    valuesSet = new Set(value.split("+"));
  }

  const result = Array.from(valuesSet).join("+");
  if (result) merged[key] = result;
  else delete merged[key];
};

const extractUrlAndSessionValues = (
  searchParams: URLSearchParams,
  key: string,
  value: string,
  crossSubject?: boolean,
  fromSearchLabel?: boolean,
): KeyValueObject => {
  const paramsObject = parseQueryParams(searchParams?.toString());
  const sessionObject = getSessionParams();
  const mergedObject = mergeTwoObjects(paramsObject, sessionObject);
  //If it is called from searchLabel no need to check the values, search labels component always used to remove values not to add
  if (
    fromSearchLabel &&
    key !== keyName?.subject &&
    key !== keyName?.location
  ) {
    delete mergedObject[key];
    if (key === "postcode") {
      delete mergedObject["distance"];
    }
  }
  if (crossSubject || key === keyName?.keyword) {
    delete mergedObject[keyName?.subject];
  }

  if (mergedObject[key] && (key !== keyName?.location || fromSearchLabel)) {
    updateMergedKeyValue(mergedObject, key, value);
  } else {
    mergedObject[key] = value;
  }

  return mergedObject;
};

const getDecodedCookie = (name: string) => {
  if (typeof document === "undefined") return null;
  const cookie = document?.cookie
    ?.split("; ")
    ?.find((row) => row?.startsWith(name + "="));
  return cookie ? cookie.split("=")[1]?.trim() || null : null;
};

const mergeTwoObjects = (
  paramsObject: KeyValueObject,
  sessionObject: KeyValueObject = {},
): KeyValueObject => {
  return {
    ...paramsObject,
    ...Object.fromEntries(
      Object.entries(sessionObject).map(([k, v]) => [
        k,
        paramsObject[k]
          ? Array.from(
              new Set([...paramsObject[k]?.split("+"), ...v?.split("+")]),
            )?.join("+")
          : v,
      ]),
    ),
  };
};

const isSingleSelection = (searchParams: URLSearchParams): boolean => {
  const entriesArray = Array.from(searchParams.entries());
  for (const [_, value] of entriesArray) {
    const decodedValue = decodeURIComponent(value);
    if (decodedValue.includes("+") || decodedValue.includes(" ")) {
      return false;
    }
  }
  return true;
};

const getFilterValue = (key: string, searchParams: URLSearchParams): string => {
  let sessionFilter: any = {};
  if (typeof window !== "undefined") {
    sessionFilter = JSON.parse(sessionStorage?.getItem("filter_param") || "{}");
  }
  const searchValue: any = searchParams?.get(key)?.split(" ") || "";
  const sessionValue: any = sessionFilter[key]?.split("+") || "";
  const returnObject: any = [...searchValue, ...sessionValue]?.filter(Boolean);
  return returnObject?.length === 1 ? returnObject?.[0] : returnObject;
};

const getParentSubject = (
  searchParams: any,
  jsondata: any,
  subjectTextKey?: any,
) => {
  if (searchParams) {
    const firstSubject =
      searchParams?.get(keyName?.subject)?.split(" ")?.[0] ||
      searchParams?.get(keyName?.keyword)?.split(" ")?.[0];
    if (firstSubject) {
      return (
        jsondata?.subjectFilterList?.find(
          (item: any) => item?.subjectTextKey === firstSubject,
        )?.parentSubject || null
      );
    }
  }
  if (subjectTextKey) {
    return (
      jsondata?.subjectFilterList?.find(
        (item: any) => item?.subjectTextKey === subjectTextKey,
      )?.parentSubject || null
    );
  }
  return null;
};

async function getUserLocation() {
  if (!navigator.geolocation) {
    console.warn("Geolocation is not supported by your browser.");
    return;
  }
  try {
    const position = await new Promise<GeolocationPosition>(
      (resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      },
    );
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const data = await makeApiCall({
      url: `${process.env.NEXT_PUBLIC_DOMSERVICE_API_DOMAIN}${API_URL?.getPostcode}`,
      method: "POST",
      queryParams: {},
      body: {
        userCoordinates: `${latitude},${`${longitude}`?.includes("-") ? longitude : -longitude}`,
      },
      headers: {
        "x-api-key": `${process.env.NEXT_PUBLIC_DOMSERVICE_X_API_KEY}`,
      },
    });
    return data;
  } catch (error) {
    console.warn("Location access denied or unavailable", error);
    return null;
  }
}

function hierarchicalLocation(regions: any) {
  const regionMap = new Map();

  regions.forEach((region: any) => {
    regionMap.set(region.regionId, { ...region, children: [] });
  });

  const root: any = [];

  regions.forEach((region: any) => {
    if (region.parentRegionId) {
      const parent = regionMap.get(region.parentRegionId);
      if (parent) {
        parent.children.push(regionMap.get(region.regionId));
      }
    } else {
      return;
    }
  });
  regions.forEach((region: any) => {
    if (
      region.parentRegionId &&
      !regions.some((r: any) => r.regionId === region.parentRegionId)
    ) {
      root.push(regionMap.get(region.regionId));
    }
  });
  return root;
}

const generatePathName = (
  slug: string,
  key?: string,
  value?: string,
  searchParams?: any,
  isUniversitySelected?: boolean,
): string => {
  const parts = slug?.split("/")?.filter(Boolean);
  const basePath = `/${parts[0] || ""}`;
  const currentPage = parts[1]?.toLowerCase() || "";
  const currentSubject = searchParams?.get(keyName?.subject)?.split(" ");
  const isSingleSubject =
    currentSubject?.length === 1 && currentSubject[0] === value;
  const isPgs = basePath === "/pgs";
  const isCSearch = currentPage === "csearch";
  const isSearch = currentPage === "search";
  const isUniKey = key === keyName?.university;

  if (isSingleSubject) return basePath;
  if (isPgs) return slug?.replace(/\/$/, "");

  if (isUniKey) {
    if (isCSearch && isUniversitySelected) return `${basePath}/search`;
    if (isSearch && !isUniversitySelected) return `${basePath}/csearch`;
    return `${basePath}/${currentPage || "csearch"}`;
  }

  if (isCSearch) return `${basePath}/csearch`;
  return key ? `${basePath}/search` : basePath;
};

function determineLocationType(regions: any, cities: any, searchParams: any) {
  const params = new URLSearchParams(searchParams);
  const location = params?.get("location")?.split(" ")[0] || "";
  if (!location) {
    return { type: null, message: "No location parameter provided" };
  }
  const regionMatch = regions?.find(
    (region: any) => region?.regionTextKey === location,
  );
  if (regionMatch) {
    return "region";
  }
  const cityMatch = cities?.find((city: any) => city?.cityTextKey === location);
  if (cityMatch) {
    return "city";
  }
  return "unknown";
}

function getParentAndChildRegions(
  filterState: any,
  regionList: any,
): { parent: string[]; child: string[] } {
  const parent: string[] = [];
  const child: string[] = [];

  const appliedLocationStr = filterState?.filterOrder?.[keyName?.location];
  if (!appliedLocationStr) return { parent, child };

  const appliedLocations = appliedLocationStr?.split("+");

  const findRegionByTextKey = (regionTextKey: string) => {
    return regionList?.find(
      (region: any) => region?.regionTextKey === regionTextKey,
    );
  };

  const getChildrenRegionTextKeys = (parentRegion: any): string[] => {
    return (
      regionList
        ?.filter(
          (region: any) => region?.parentRegionId === parentRegion?.regionId,
        )
        ?.map((region: any) => region?.regionTextKey) || []
    );
  };

  appliedLocations.forEach((regionTextKey: string) => {
    const selectedRegion = findRegionByTextKey(regionTextKey);
    if (!selectedRegion) return;

    const isParent = selectedRegion?.parentRegionId === 1;

    if (isParent) {
      parent.push(regionTextKey);
      const children = getChildrenRegionTextKeys(selectedRegion);
      child.push(...children);
    } else {
      child.push(regionTextKey);
    }
  });

  return { parent, child };
}

const modifySearchParams = (key: string, value: string, urlParams: any) => {
  const searchparamObject = Object.fromEntries(urlParams?.entries());
  searchparamObject[key] = value;
  const modifiedParam = new URLSearchParams(searchparamObject);
  return `${modifiedParam}`;
};

const constructSearchParams = (orderedFilters: KeyValueObject) => {
  const urlParams = new URLSearchParams();
  const cookieParams: KeyValueObject = {};
  let totalValues = 0;

  const atomicKeys = ["postcode"];
  const sortKeys = ["subject", "location"];

  Object.entries(orderedFilters)?.forEach(([k, v]) => {
    if (atomicKeys.includes(k)) {
      urlParams.set(k, v);
      return;
    }

    let valuesArray = v?.split("+") ?? [];

    if (sortKeys.includes(k)) {
      valuesArray.sort((a, b) => a.localeCompare(b));
    }

    if (totalValues + valuesArray.length <= 4) {
      urlParams.set(k, valuesArray.join("+"));
      totalValues += valuesArray.length;
    } else {
      const allowedValues = valuesArray.slice(0, 4 - totalValues);
      const remainingValues = valuesArray.slice(4 - totalValues);

      if (allowedValues.length > 0) {
        urlParams.set(k, allowedValues.join("+"));
        totalValues += allowedValues.length;
      }

      if (remainingValues.length > 0) {
        cookieParams[k] = remainingValues.join("+");
      }
    }
  });

  return { urlParams, cookieParams };
};

const checkIfParentSubjectChanged = (
  key: string,
  value: string,
  jsondata: any,
  searchParams: URLSearchParams,
) => {
  if (key == keyName?.subject) {
    const selectedParent = getParentSubject(null, jsondata, value);
    const currentParent = getParentSubject(searchParams, jsondata);
    if (selectedParent && currentParent) {
      return selectedParent !== currentParent;
    } else if (
      !selectedParent &&
      !currentParent &&
      getFilterValue(keyName?.subject, searchParams) == value
    ) {
      return false;
    } else {
      return true;
    }
  } else {
    return false;
  }
};

const handleCookiesAndSession = (cookieParams: KeyValueObject) => {
  setDocumentCookie({
    name: "filter_param",
    value: JSON.stringify(cookieParams),
  });
  sessionStorage.setItem("filter_param", JSON.stringify(cookieParams));
};

const universitiesSortingList: any = (jsondata: any) => {
  const listvalue: any[] = [];

  const stripPrefix = (name: string): string => {
    let cleanedName = name.trim();
    for (const prefix of IGNORED_PREFIXES) {
      if (cleanedName.toLowerCase().startsWith(prefix.toLowerCase())) {
        return cleanedName.slice(prefix.length).trim();
      }
    }
    return cleanedName;
  };

  UNI_SORTING_MOCKDATA?.map((item: any) => {
    item.unilist = jsondata?.universityFilterList?.filter(
      (collegeItem: any) => {
        const normalizedName = stripPrefix(collegeItem?.collegeName || "");
        const regex = new RegExp(`^[${item.sortingValue}]`, "i");
        return regex.test(normalizedName);
      },
    );
    listvalue.push(item);
  });

  return listvalue;
};

const refreshSearchFilter = (inputUrl: string | undefined) => {
  REFRESH_PARAMETERS?.map((refreshValues: string) => {
    if (inputUrl?.includes(refreshValues) && typeof inputUrl === "string") {
      // Extract parameters from URL path structure
      const params: any = {};
      const pathParts = inputUrl.split("/").filter(Boolean);

      const hasPGS = isPGS();
      const hasWhatuni = isWhatuni();
      const isCsearch = pathParts[2] === "csearch";

      // Parse scholarship URL structure
      if ((hasWhatuni || hasPGS) && pathParts[1] === "scholarships") {
        // For PGS (funding), default to masters, no study level in URL
        if (hasPGS) {
          params.studyLevel = "masters";
          // For PGS: /funding/scholarships/search/subject or /funding/scholarships/csearch/university
          if (isCsearch) {
            if (pathParts[3]) {
              params.university = pathParts[3];
            }
          } else {
            if (pathParts[3]) {
              params.subject = pathParts[3];
            }
            if (pathParts[4]) {
              params.university = pathParts[4];
            }
          }
        } else {
          // For money URLs: /money/scholarships/search/undergraduate/subject/university
          if (pathParts[3]) {
            const studyLevel =
              pathParts[3] === "postgraduate" ? "masters" : pathParts[3];
            params.studyLevel = studyLevel;
          }
          if (isCsearch) {
            if (pathParts[4]) {
              params.university = pathParts[4];
            }
          } else {
            if (pathParts[4]) {
              params.subject = pathParts[4];
            }
            if (pathParts[5]) {
              params.university = pathParts[5];
            }
          }
        }
      }
      if (pathParts[1] === "scholarships") {
        emitter.emit("refreshScholarshipFilters", params);
      } else {
        emitter.emit("refreshFilters");
      }
      return;
    }
  });
};

export {
  universitiesSortingList,
  refreshSearchFilter,
  getParentAndChildRegions,
  handleCookiesAndSession,
  checkIfParentSubjectChanged,
  constructSearchParams,
  modifySearchParams,
  getFilterValue,
  generatePathName,
  hierarchicalLocation,
  determineLocationType,
  getUserLocation,
  mergeTwoObjects,
  isSingleSelection,
  getDecodedCookie,
  getParentSubject,
  extractUrlAndSessionValues,
};
